import {
  createUserDataLabel,
  getMarbleContent,
  isString,
  UserMarble,
} from "@hoylu/client-common";
import { clsx } from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Dictionary } from "ts-essentials";
import {
  PROJECT_ADMIN_ID,
  PROJECT_CONTRIBUTOR_ID,
  PROJECT_VIEWER_ID,
} from "../../../constants/projectRoles.constant";
import type {
  MyWorkspaceMetadata,
  WorkspaceRole,
} from "../../../state/workspaces/types";
import {
  removeWorkspaceUsers,
  upsertWorkspaceUsers,
} from "../../../state/workspaces/workspaces.actions";
import {
  selectIsUpdatingWorkspace,
  selectPublicPermissionLevel,
} from "../../../state/workspaces/workspaces.selector";
import { useI18n } from "../../../utils/hooks/use.i18n";
import type { RoleUserPermission } from "../../../utils/sharing.helpers";
import {
  getShareOptionFromWorkspaceRole,
  getWorkspaceRoleFromShareOption,
} from "../../../utils/sharing.helpers";
import type { PermissionOptionProps } from "./PermissionOption";
import { PermissionsBar } from "./PermissionsBar";
import { ShareType } from "./ShareType";
import Styles from "./UserPermissionsBar.module.css";

interface UserPermissionsBarProps {
  isAdmin: boolean;
  options: ShareOptionValue[];
  removable?: boolean;
  user: RoleUserPermission;
  workspace: MyWorkspaceMetadata;
}

const createPermissionLabel = (user: RoleUserPermission) =>
  user.firstName || user.lastName
    ? [user.firstName, user.lastName].filter(Boolean).join(" ")
    : user.email;

const ProjectRoleIdToClassName: Dictionary<WorkspaceRole> = {
  [PROJECT_ADMIN_ID]: "administer",
  [PROJECT_CONTRIBUTOR_ID]: "write",
  [PROJECT_VIEWER_ID]: "read",
};

export const UserPermissionsBar = ({
  isAdmin,
  options,
  removable,
  user,
  workspace,
}: UserPermissionsBarProps) => {
  const dispatch = useDispatch();

  const tPermissionBar = useI18n("PERMISSION_BAR.");
  const tShareType = useI18n("SHARE_TYPE.");

  const isUpdatingWorkspace = useSelector(selectIsUpdatingWorkspace);
  const publicPermissionLevel = useSelector(selectPublicPermissionLevel);

  useEffect(() => {
    if (!isUpdatingWorkspace) {
      setShowLoadingSpinner(false);
    }
  }, [isUpdatingWorkspace]);

  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);

  const explicitOption = getShareOptionFromWorkspaceRole(user.currentRole);
  let effectiveOption =
    publicPermissionLevel.value > explicitOption.value
      ? publicPermissionLevel
      : explicitOption;
  // if the effective permission is not in the list of available options, take the top one (the most permissive)
  // this is until the service responds with both the explicit and effective permissions, then we can remove this fallback
  if (
    !user.allowedWorkspaceRoles?.includes(
      getWorkspaceRoleFromShareOption(effectiveOption),
    )
  ) {
    effectiveOption = getShareOptionFromWorkspaceRole(
      user.allowedWorkspaceRoles?.[0] ?? "none",
    );
  }

  const removeAccess = useCallback(
    (email: string) => {
      dispatch(
        removeWorkspaceUsers.request({
          workspaceId: workspace.workspaceId,
          userEmails: [email],
        }),
      );
    },
    [dispatch, workspace.workspaceId],
  );

  const updateAccess = useCallback(
    (userEmails: string[], permission: WorkspaceRole) => {
      dispatch(
        upsertWorkspaceUsers.request({
          workspaceId: workspace.workspaceId,
          userEmails,
          permission,
        }),
      );
    },
    [dispatch, workspace.workspaceId],
  );

  const updateUserAccess = useCallback(
    (shareOption: ShareOptionValue) => {
      setShowLoadingSpinner(true);
      if (shareOption.value === ShareType.REMOVE.value) {
        removeAccess(user.email);
      } else {
        updateAccess(
          [user.email],
          getWorkspaceRoleFromShareOption(shareOption),
        );
      }
    },
    [updateAccess, removeAccess, user],
  );

  const mappedOptions = useMemo(() => {
    return options
      .filter((option) =>
        user.allowedWorkspaceRoles?.includes(
          getWorkspaceRoleFromShareOption(option),
        ),
      )
      .map<PermissionOptionProps>((option) => {
        const isExplicit = explicitOption.value === option.value;
        const isSelected = effectiveOption.value === option.value;
        return {
          ...option,
          disabled: !isAdmin || isExplicit,
          highlight: isAdmin && isExplicit,
          inactive: !isAdmin,
          onClick: () => updateUserAccess(option),
          selected: isSelected,
          title: tShareType(option.title),
          tooltip:
            (isSelected || isAdmin || undefined) &&
            option.tooltip &&
            [
              tShareType(option.tooltip),
              tPermissionBar(
                workspace.containerId
                  ? "ONLY_PROJECT_ADMINS_CHANGE_ACCESS"
                  : "ONLY_ADMINS_CHANGE_ACCESS",
              ),
            ].join("<br /><br />"),
        };
      });
  }, [
    effectiveOption.value,
    explicitOption.value,
    isAdmin,
    options,
    tPermissionBar,
    tShareType,
    updateUserAccess,
    user.allowedWorkspaceRoles,
    workspace.containerId,
  ]);

  return (
    <PermissionsBar
      containerProps={{
        title: tPermissionBar(
          workspace.containerId
            ? "ONLY_PROJECT_ADMINS_CHANGE_ACCESS"
            : "ONLY_ADMINS_CHANGE_ACCESS",
        ),
      }}
      isLoading={showLoadingSpinner}
      label={
        <div className={Styles.userContainer} title={createUserDataLabel(user)}>
          <UserMarble className={Styles.userMarble} email={user.email}>
            {getMarbleContent(
              ...(user.firstName || user.lastName
                ? [user.firstName, user.lastName].filter(isString)
                : [user.email]),
            )}
          </UserMarble>
          <div className={Styles.userLabel}>
            <div className="truncate">{createPermissionLabel(user)}</div>
            <div className={clsx(Styles.userSubLabel, "truncate")}>
              {user.orgName}
              {!!user.containerRole && (
                <>
                  {!!user.orgName && ", "}
                  <span
                    className={clsx(
                      Styles.role,
                      ProjectRoleIdToClassName[user.containerRole.id],
                    )}
                  >
                    {user.containerRole.name}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      }
      options={mappedOptions}
    >
      {isAdmin && (
        <button
          className={clsx(
            workspace.containerId ? "hoylu-ui-icons-reset" : "hoylu-ui-icons-x",
            Styles.close,
            {
              invisible: !removable,
            },
          )}
          onClick={() => updateUserAccess(ShareType.REMOVE)}
          title={tPermissionBar(
            workspace.containerId
              ? "REMOVE_PROJECT_WORKSPACE_ACCESS"
              : "REMOVE_WORKSPACE_ACCESS",
          )}
          type="button"
        />
      )}
    </PermissionsBar>
  );
};
